import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Minus, Plus, ShoppingBag, Loader2, Clock, AlertCircle, MoonStar, Sun, Briefcase,
} from 'lucide-react';
import axios from 'axios';
import { useCart } from '../contexts/CartContext';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICONS = {
  'prod-checkin-14': Sun,
  'prod-checkin-12': Sun,
  'prod-checkout-12': MoonStar,
  'prod-bagage': Briefcase,
};


const ServiceRow = ({ product, quantity, onQuantityChange }) => {
  const Icon = ICONS[product.id] || Clock;
  return (
    <div
      className="flex flex-col sm:flex-row sm:items-center justify-between p-5 bg-white rounded-xl border border-gray-100 hover:border-[#5493CC]/40 transition-colors"
      data-testid={`service-row-${product.id}`}
    >
      <div className="flex-1 mb-4 sm:mb-0">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#F2F8FD] flex items-center justify-center flex-shrink-0">
            <Icon className="h-5 w-5 text-[#5493CC]" />
          </div>
          <div>
            <h3 className="font-['Playfair_Display'] font-semibold text-lg text-[#2D3E50]">
              {product.name}
            </h3>
            <p className="text-sm text-[#64748B] mt-1 whitespace-pre-line">
              {product.description}
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-6 sm:gap-8">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9 rounded-lg border-gray-200"
            onClick={() => onQuantityChange(product.id, Math.max(0, quantity - 1))}
            data-testid={`decrease-${product.id}`}
          >
            <Minus className="h-4 w-4" />
          </Button>
          <span className="w-8 text-center font-semibold text-[#2D3E50]" data-testid={`qty-${product.id}`}>
            {quantity}
          </span>
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9 rounded-lg border-gray-200"
            onClick={() => onQuantityChange(product.id, quantity + 1)}
            data-testid={`increase-${product.id}`}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        <div className="text-right min-w-[80px]">
          <span className="text-xl font-semibold text-[#2D3E50]">
            {product.price.toFixed(2)} €
          </span>
        </div>
      </div>
    </div>
  );
};


const CheckInOutPage = () => {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { toast } = useToast();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantities, setQuantities] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/products`);
        setProducts(res.data.filter((p) => p.category === 'check-in-out'));
      } catch (e) {
        toast({
          title: 'Erreur',
          description: 'Impossible de charger les services.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [toast]);

  const handleQuantityChange = (productId, qty) => {
    setQuantities((prev) => ({ ...prev, [productId]: qty }));
  };

  const totalItems = Object.values(quantities).reduce((acc, q) => acc + q, 0);
  const totalPrice = products.reduce(
    (acc, p) => acc + p.price * (quantities[p.id] || 0),
    0,
  );

  const handleAddToCart = () => {
    products.forEach((p) => {
      const qty = quantities[p.id] || 0;
      if (qty > 0) addItem(p, qty);
    });
    setQuantities({});
    toast({
      title: 'Services ajoutés au panier',
      description: 'Finalisez votre commande pour confirmer.',
    });
    navigate('/products');
  };

  return (
    <div className="min-h-screen pt-20 md:pt-24 pb-12 bg-[#FAFAFA]">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#2D3E50] to-[#3D5266] text-white py-16 md:py-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#E8945A] font-medium mb-3">
            <Clock className="h-4 w-4" />
            Services additionnels
          </span>
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl tracking-tight font-medium">
            Check-in & Check-out
          </h1>
          <p className="text-white/70 mt-4 max-w-2xl text-lg">
            Arrivez plus tôt, partez plus tard. Profitez pleinement de votre séjour
            sans contrainte d'horaire.
          </p>
        </div>
      </section>

      {/* Restriction notice */}
      <div className="max-w-4xl mx-auto px-6 md:px-12 mt-8">
        <div
          className="rounded-xl border-2 border-[#E8945A]/40 bg-[#FFFBF5] p-5 flex gap-3"
          data-testid="checkinout-restriction"
        >
          <AlertCircle className="h-5 w-5 text-[#E8945A] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-[#2D3E50]">
              Service réservé aux conciergeries partenaires &amp; logements MCB
            </p>
            <p className="text-sm text-[#475569] mt-1">
              Les options Check-in et Check-out ne sont disponibles que dans les
              logements gérés par Terre et Mer Conciergerie ou par MCB. Pour toute autre
              location, contactez d'abord votre propriétaire ou votre conciergerie pour
              vérifier la possibilité d'arrivée anticipée ou départ tardif.
            </p>
          </div>
        </div>
      </div>

      {/* Services list */}
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-10">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-[#E8945A]" />
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {products.map((p) => (
                <ServiceRow
                  key={p.id}
                  product={p}
                  quantity={quantities[p.id] || 0}
                  onQuantityChange={handleQuantityChange}
                />
              ))}
            </div>

            {/* Add to cart bar */}
            {totalItems > 0 && (
              <div
                className="mt-6 rounded-xl border border-[#E8945A]/30 bg-white p-5 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-sm sticky bottom-4"
                data-testid="checkinout-cart-bar"
              >
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#64748B]">
                    {totalItems} service{totalItems > 1 ? 's' : ''} sélectionné{totalItems > 1 ? 's' : ''}
                  </div>
                  <div className="font-['Playfair_Display'] text-2xl font-medium text-[#E8945A] mt-1">
                    {totalPrice.toFixed(2)} €
                  </div>
                </div>
                <Button
                  onClick={handleAddToCart}
                  className="bg-[#E8945A] hover:bg-[#D4844A] gap-2 h-12 px-6"
                  data-testid="checkinout-add-cart"
                >
                  <ShoppingBag className="h-4 w-4" />
                  Ajouter au panier
                </Button>
              </div>
            )}
          </>
        )}

        {/* Help footer */}
        <div className="mt-12 rounded-2xl bg-gradient-to-br from-[#FFFBF5] to-white border border-[#E8945A]/20 p-8 md:p-10 text-center">
          <h3 className="font-['Playfair_Display'] text-2xl font-medium text-[#2D3E50] mb-3">
            Besoin de vérifier l'éligibilité de votre logement ?
          </h3>
          <p className="text-[#64748B] mb-6 max-w-xl mx-auto">
            Notre équipe valide en quelques heures si votre logement permet
            ce service. Envoyez-nous l'adresse, on revient vers vous.
          </p>
          <Button
            asChild
            className="bg-[#2D3E50] hover:bg-[#3D4F61] rounded-lg h-12 px-8"
            data-testid="checkinout-contact"
          >
            <a href="mailto:contact@terreetmer-locationdelinge.fr?subject=Disponibilité%20Check-in/Check-out">
              Vérifier l'éligibilité
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CheckInOutPage;
